<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import { getUnixTime } from 'date-fns';
import { CMD_SNOOZE_NOTIFICATION } from 'dashboard/helper/commandbar/events';
import wootConstants from 'dashboard/constants/globals';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import PaginationButton from './PaginationButton.vue';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal.vue';
import { emitter } from 'shared/helpers/mitt';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxKeyboardGuide from './InboxKeyboardGuide.vue';

export default {
  components: {
    PaginationButton,
    NextButton,
    BackButton,
    InboxKeyboardGuide,
    CustomSnoozeModal,
  },
  props: {
    totalLength: {
      type: Number,
      default: 0,
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    activeNotification: {
      type: Object,
      default: null,
    },
  },
  emits: ['next', 'prev'],
  data() {
    return { showCustomSnoozeModal: false };
  },
  computed: {
    ...mapGetters({ meta: 'notifications/getMeta' }),
  },
  mounted() {
    emitter.on(CMD_SNOOZE_NOTIFICATION, this.onCmdSnoozeNotification);
  },
  unmounted() {
    emitter.off(CMD_SNOOZE_NOTIFICATION, this.onCmdSnoozeNotification);
  },
  methods: {
    openSnoozeNotificationModal() {
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'snooze_notification' });
    },
    hideCustomSnoozeModal() {
      this.showCustomSnoozeModal = false;
    },
    async snoozeNotification(snoozedUntil) {
      try {
        await this.$store.dispatch('notifications/snooze', {
          id: this.activeNotification?.id,
          snoozedUntil,
        });

        useAlert(this.$t('INBOX.ALERTS.SNOOZE'));
      } catch (error) {
        // Silently fail without any change in the UI
      }
    },
    onCmdSnoozeNotification(snoozeType) {
      if (snoozeType === wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME) {
        this.showCustomSnoozeModal = true;
      } else if (typeof snoozeType === 'number') {
        this.snoozeNotification(snoozeType);
      } else {
        const snoozedUntil = findSnoozeTime(snoozeType) || null;
        this.snoozeNotification(snoozedUntil);
      }
    },
    scheduleCustomSnooze(customSnoozeTime) {
      this.showCustomSnoozeModal = false;
      if (customSnoozeTime) {
        const snoozedUntil = getUnixTime(customSnoozeTime) || null;
        this.snoozeNotification(snoozedUntil);
      }
    },
    async deleteNotification() {
      useTrack(INBOX_EVENTS.DELETE_NOTIFICATION);
      try {
        await this.$store.dispatch('notifications/delete', {
          notification: this.activeNotification,
          unreadCount: this.meta.unreadCount,
          count: this.meta.count,
        });

        useAlert(this.$t('INBOX.ALERTS.DELETE'));
        this.$router.replace({ name: 'inbox_view' });
      } catch {
        useAlert(this.$t('INBOX.ALERTS.DELETE_ERROR'));
      }
    },
    onClickNext() {
      this.$emit('next');
    },
    onClickPrev() {
      this.$emit('prev');
    },
    onClickGoToInboxList() {
      this.$router.replace({ name: 'inbox_view' });
    },
  },
};
</script>

<template>
  <div
    class="grid items-center w-full grid-cols-[auto_minmax(0,1fr)_auto] gap-3 px-3 border-b h-14 rtl:border-r border-n-weak flex-shrink-0 bg-n-solid-2"
  >
    <div class="flex items-center gap-3">
      <BackButton
        compact
        :button-label="$t('INBOX.ACTION_HEADER.BACK')"
        class="xl:hidden flex"
      />
      <PaginationButton
        v-if="totalLength > 1"
        :total-length="totalLength"
        :current-index="currentIndex + 1"
        @next="onClickNext"
        @prev="onClickPrev"
      />
    </div>
    <InboxKeyboardGuide class="justify-center" />
    <div class="flex items-center justify-end gap-1">
      <NextButton
        v-tooltip.bottom="$t('INBOX.ACTION_HEADER.SNOOZE')"
        :label="$t('INBOX.ACTION_HEADER.SNOOZE')"
        icon="i-lucide-bell-minus"
        slate
        sm
        faded
        class="[&>.truncate]:hidden 2xl:[&>.truncate]:block"
        @click="openSnoozeNotificationModal"
      />
      <NextButton
        v-tooltip.bottom="$t('INBOX.ACTION_HEADER.DELETE')"
        :label="$t('INBOX.ACTION_HEADER.DELETE')"
        icon="i-lucide-trash-2"
        slate
        sm
        faded
        class="[&>.truncate]:hidden 2xl:[&>.truncate]:block"
        @click="deleteNotification"
      />
    </div>
    <CustomSnoozeModal
      v-if="showCustomSnoozeModal"
      @close="hideCustomSnoozeModal"
      @choose-time="scheduleCustomSnooze"
    />
  </div>
</template>
